import { cn } from "@/lib/utils";

interface FloatingShapesProps {
  variant?: "hero" | "subtle" | "accent" | "primary";
  density?: "low" | "medium" | "high";
  className?: string;
}

const FloatingShapes = ({ variant = "hero", density = "medium", className }: FloatingShapesProps) => {
  const shapes = {
    hero: [
      { size: "w-72 h-72", position: "top-20 -left-20", delay: "0s", color: "bg-accent/10", duration: "20s" },
      { size: "w-96 h-96", position: "-bottom-20 -right-20", delay: "2s", color: "bg-accent/5", duration: "25s" },
      { size: "w-48 h-48", position: "top-1/3 right-1/4", delay: "1s", color: "bg-primary-foreground/5", duration: "18s" },
      { size: "w-32 h-32", position: "bottom-1/4 left-1/3", delay: "3s", color: "bg-accent/8", duration: "22s" },
      { size: "w-24 h-24", position: "top-1/2 left-1/4", delay: "4s", color: "bg-accent/6", duration: "15s" },
    ],
    subtle: [
      { size: "w-64 h-64", position: "top-10 right-10", delay: "0s", color: "bg-accent/5", duration: "20s" },
      { size: "w-48 h-48", position: "bottom-20 left-20", delay: "1.5s", color: "bg-primary/5", duration: "25s" },
      { size: "w-32 h-32", position: "top-1/2 right-1/3", delay: "2.5s", color: "bg-accent/3", duration: "18s" },
    ],
    accent: [
      { size: "w-40 h-40", position: "top-0 right-0", delay: "0s", color: "bg-accent/15", duration: "18s" },
      { size: "w-56 h-56", position: "bottom-0 left-0", delay: "1s", color: "bg-accent/10", duration: "22s" },
      { size: "w-24 h-24", position: "top-1/2 left-1/2", delay: "2s", color: "bg-accent/20", duration: "15s" },
      { size: "w-36 h-36", position: "top-1/4 right-1/4", delay: "3s", color: "bg-accent-foreground/5", duration: "20s" },
    ],
    primary: [
      { size: "w-72 h-72", position: "top-10 -left-10", delay: "0s", color: "bg-primary-foreground/5", duration: "25s" },
      { size: "w-96 h-96", position: "-bottom-10 -right-10", delay: "2s", color: "bg-accent/10", duration: "30s" },
      { size: "w-48 h-48", position: "top-1/4 right-1/3", delay: "1s", color: "bg-primary-foreground/3", duration: "20s" },
      { size: "w-40 h-40", position: "bottom-1/3 left-1/4", delay: "2.5s", color: "bg-accent/5", duration: "22s" },
      { size: "w-28 h-28", position: "top-2/3 right-1/4", delay: "4s", color: "bg-accent/8", duration: "18s" },
    ],
  };

  const densityMap = { low: 2, medium: 4, high: 5 };
  const maxShapes = densityMap[density];
  const selectedShapes = shapes[variant].slice(0, maxShapes);

  return (
    <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      {selectedShapes.map((shape, index) => (
        <div
          key={index}
          className={cn(
            "absolute rounded-full blur-3xl animate-float-slow",
            shape.size,
            shape.position,
            shape.color
          )}
          style={{ animationDelay: shape.delay }}
        />
      ))}
    </div>
  );
};

export default FloatingShapes;
